import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
	readPublicRetrievalStatus,
	UserDocument,
} from '@/entities/user-document';
import { useSidebar } from '@/provider/sidebar/sidebar-context';
import {
	getUserDocuments,
	LastEvaluatedKey,
} from '@/use-cases/get-user-pending-to-sign-documents';
import { getSignedDocumentDownloadUrl } from '@/use-cases/get-signed-document-download-url';
import styles from './my-documents.module.scss';
import { DataTable } from '@/components/data-table/data-table';
import { ColumnDef } from '@tanstack/react-table';
import {
	ArrowUpDown,
	History,
	ArrowDownToLine,
	FileText,
	UserPlus,
	LockKeyhole,
} from 'lucide-react';
import { useHistory } from 'react-router-dom';
import { DateTime } from 'luxon';
import routeConfig from '@/config/routeConfig';
import notifyHB from '@/config/notifyHB';
import { DocumentAuditLogs } from './components/document-audit-logs/document-audit-logs';
import { useWindowDimensions } from '@/hooks/use-window-dimensions';
import { MobileDocumentsList } from './components/mobile-documents-list/mobile-documents-list';
import { AddSignerDialog } from './components/add-signer-dialog/add-signer-dialog';
import { DocumentStateBadge } from './components/document-state-badge/document-state-badge';
import { PublicRetrievalBadge } from './components/public-retrieval-badge/public-retrieval-badge';
import { RevokePublicRetrievalDialog } from './components/revoke-public-retrieval-dialog/revoke-public-retrieval-dialog';
import { hasSeenTour, startTour } from '@/hooks/use-onboarding-tour';
import { DriveStep } from 'driver.js';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const DESKTOP_TOUR_STEPS: DriveStep[] = [
	{
		element: '[data-tour="welcome"]',
		popover: {
			title: 'Bienvenido a mis documentos',
			description:
				'En esta sección podrás crear y organizar los documentos a firmar.',
		},
	},
	{
		element: '[data-tour="new-document"]',
		popover: {
			title: 'Crea un nuevo documento',
			description:
				'Selecciona el botón para subir tu primer documento a firma.',
		},
	},
];

const formatDate = (value: string | null) =>
	value
		? DateTime.fromISO(value).toLocaleString(DateTime.DATETIME_MED)
		: '-';

export const MyDocuments = () => {
	const { hideSidebar } = useSidebar();
	const [userDocuments, setUserDocuments] = useState<UserDocument[]>([]);
	const [selectedDocument, setSelectedDocument] =
		useState<UserDocument | null>(null);
	const [documentToAddSigner, setDocumentToAddSigner] =
		useState<UserDocument | null>(null);
	const [documentToRevoke, setDocumentToRevoke] =
		useState<UserDocument | null>(null);
	const [currentLastEvaluatedKey, setCurrentLastEvaluatedKey] =
		useState<LastEvaluatedKey | null>(null);
	const [pagesSk, setPagesSk] = useState<{
		[page: number]: LastEvaluatedKey;
	}>({});
	const [page, setPage] = useState(0);
	const [isFetchingDocuments, setIsFetchingDocuments] = useState(false);
	const [tab, setTab] = useState<'Signed' | 'Pending'>('Pending');
	const history = useHistory();
	const { isResponsive } = useWindowDimensions();

	useEffect(() => {
		if (hasSeenTour('my-documents')) return;
		startTour('my-documents', DESKTOP_TOUR_STEPS);
	}, []);

	const downloadDocument = (document: UserDocument) => {
		const path = `${document.Owner}/${document.Serial}/signed/${document.Path}`;
		getSignedDocumentDownloadUrl(path)
			.then((url) => {
				if (url) window.open(url, '_blank');
			})
			.catch((error) =>
				notifyHB(
					'my-documents',
					'MyDocuments',
					`Fail directly download document with ${error}`,
				),
			);
	};

	// Withdrawing is irreversible, so the row is corrected in place rather than
	// refetched: a stale list would leave the action armed on a document that
	// no longer has a public copy.
	const markDocumentWithdrawn = (
		document: UserDocument,
		publicRevokedAt: string | null,
	) =>
		setUserDocuments((current) =>
			current.map((candidate) =>
				candidate.Serial === document.Serial
					? {
							...candidate,
							public_retrieval: false,
							public_revoked_at:
								publicRevokedAt ??
								candidate.public_revoked_at ??
								new Date().toISOString(),
						}
					: candidate,
			),
		);

	const sortableHeader =
		(label: string): ColumnDef<UserDocument>['header'] =>
		({ column }) => (
			<Button
				className="-ml-3"
				onClick={() =>
					column.toggleSorting(column.getIsSorted() === 'asc')
				}
				size="sm"
				variant="ghost"
			>
				{label}
				<ArrowUpDown className="ml-1 h-3.5 w-3.5" />
			</Button>
		);

	const myDocumentsTableData: ColumnDef<UserDocument>[] = [
		{
			accessorKey: 'Path',
			header: sortableHeader('Nombre'),
			cell: ({ row }) => (
				<div className={styles['name-cell']}>
					<FileText className={styles['name-icon']} />
					<span
						className={styles['name-text']}
						title={row.getValue('Path')}
					>
						{row.getValue('Path')}
					</span>
				</div>
			),
		},
		{
			accessorKey: 'State',
			header: 'Estado',
			cell: ({ row }) => (
				<div className={styles['state-cell']}>
					<DocumentStateBadge state={row.getValue('State')} />
					<PublicRetrievalBadge
						status={readPublicRetrievalStatus(row.original)}
					/>
				</div>
			),
		},
		{
			accessorKey: 'CreatedAt',
			header: sortableHeader('Fecha de creación'),
			cell: ({ row }) => (
				<span className={styles['date-cell']}>
					{formatDate(row.getValue('CreatedAt'))}
				</span>
			),
		},
		{
			accessorKey: 'SignedAt',
			header: sortableHeader('Fecha de firma'),
			cell: ({ row }) => (
				<span className={styles['date-cell']}>
					{formatDate(row.getValue('SignedAt'))}
				</span>
			),
		},
		{
			id: 'actions',
			header: '',
			cell: ({ row }) => (
				<div className={styles['actions-cell']}>
					{row.original.State === 'Signed' && (
						<Button
							variant="outline"
							size="sm"
							onClick={() => setDocumentToAddSigner(row.original)}
						>
							<UserPlus />
							Agregar firmante
						</Button>
					)}
					{readPublicRetrievalStatus(row.original) === 'active' && (
						<Button
							variant="outline"
							size="sm"
							onClick={() => setDocumentToRevoke(row.original)}
						>
							<LockKeyhole />
							Retirar descarga pública
						</Button>
					)}
					<Button
						variant="ghost"
						size="icon"
						title="Descargar"
						aria-label="Descargar"
						onClick={() => downloadDocument(row.original)}
					>
						<ArrowDownToLine />
					</Button>
					<Button
						variant="ghost"
						size="icon"
						title="Historial"
						aria-label="Historial"
						onClick={() => setSelectedDocument(row.original)}
					>
						<History />
					</Button>
				</div>
			),
		},
	];

	const refreshUserDocuments = (
		toPage: number = 1,
		lastEvaluatedKey: LastEvaluatedKey | null = null,
		filter: typeof tab = 'Pending',
	) => {
		setIsFetchingDocuments(true);
		getUserDocuments(pagesSk[toPage - 1] ?? lastEvaluatedKey, filter)
			.then((serverUserDocuments) => {
				if (serverUserDocuments) {
					setUserDocuments(serverUserDocuments.items);
					setCurrentLastEvaluatedKey(
						serverUserDocuments.last_evaluated_key,
					);
					setPagesSk((current) => ({
						...current,
						[toPage]: serverUserDocuments.last_evaluated_key,
					}));
					setPage(toPage);
				}
			})
			.catch((error) =>
				notifyHB(
					'my-documents',
					'MyDocuments',
					`Fail to get agreement document PDF with ${error}`,
				),
			)
			.finally(() => {
				setIsFetchingDocuments(false);
			});
	};

	useEffect(() => {
		refreshUserDocuments();
	}, []);

	useEffect(() => {
		refreshUserDocuments(undefined, undefined, tab);
	}, [tab]);

	const skeleton = (
		<div className={styles['table-skeleton']}>
			{Array.from({ length: 6 }).map((_, i) => (
				<div key={i} className={styles['skeleton-row']}>
					<div
						className={styles['skeleton-cell']}
						style={{ flex: 3 }}
					/>
					<div
						className={styles['skeleton-cell']}
						style={{ flex: 1 }}
					/>
					<div
						className={styles['skeleton-cell']}
						style={{ flex: 2 }}
					/>
					<div
						className={styles['skeleton-cell']}
						style={{ flex: 2 }}
					/>
					<div
						className={styles['skeleton-cell']}
						style={{ flex: 1 }}
					/>
				</div>
			))}
		</div>
	);

	const table = isFetchingDocuments ? (
		skeleton
	) : isResponsive ? (
		<MobileDocumentsList
			onDownload={downloadDocument}
			onHistory={setSelectedDocument}
			onAddSigner={setDocumentToAddSigner}
			onRevokePublicRetrieval={setDocumentToRevoke}
			data={userDocuments}
		/>
	) : (
		<DataTable
			columns={myDocumentsTableData}
			data={userDocuments}
			isSortable
		/>
	);

	return (
		<div data-tour="welcome" className={styles['my-documents']}>
			<div className={'cyber-backdrop ' + styles.content} />
			<div className="cyber-main with-sidenav" onClick={hideSidebar}>
				<div className="cyber-spacer section"></div>
				<div className={styles.container}>
					<div className={styles.body}>
						<div className={styles.header}>
							<h1>Mis documentos</h1>
							<Button
								variant="outline"
								onClick={() =>
									history.push(routeConfig.signZip)
								}
							>
								Firmar ZIP
							</Button>
							<Button
								onClick={() =>
									history.push(routeConfig.signDocument)
								}
								data-tour="new-document"
							>
								Nuevo documento
							</Button>
						</div>
						<Tabs
							className={styles.tabs}
							value={tab}
							onValueChange={setTab}
						>
							<TabsList className={styles['tab-list']}>
								<TabsTrigger value="Pending">
									Pendientes
								</TabsTrigger>
								<TabsTrigger value="signed">
									Firmados
								</TabsTrigger>
							</TabsList>
							<TabsContent value="Pending">{table}</TabsContent>
							<TabsContent value="signed">{table}</TabsContent>
						</Tabs>
						<span>Página {page}</span>
						<div className={styles['pages-controller']}>
							<Button
								disabled={page === 1}
								variant="ghost"
								onClick={() =>
									refreshUserDocuments(
										page - 1,
										currentLastEvaluatedKey,
										tab,
									)
								}
							>
								Página anterior
							</Button>
							<Button
								variant="ghost"
								disabled={userDocuments.length < 25}
								onClick={() =>
									refreshUserDocuments(
										page + 1,
										currentLastEvaluatedKey,
										tab,
									)
								}
							>
								Siguiente página
							</Button>
						</div>
					</div>
				</div>
			</div>
			<AddSignerDialog
				document={documentToAddSigner}
				onClose={() => setDocumentToAddSigner(null)}
				onSignersAdded={() =>
					refreshUserDocuments(page, currentLastEvaluatedKey, tab)
				}
			/>
			<RevokePublicRetrievalDialog
				document={documentToRevoke}
				onClose={() => setDocumentToRevoke(null)}
				onRevoked={markDocumentWithdrawn}
			/>
			<DocumentAuditLogs
				document={selectedDocument}
				onClose={() => setSelectedDocument(null)}
				onSignersAdded={() =>
					refreshUserDocuments(page, currentLastEvaluatedKey, tab)
				}
			/>
		</div>
	);
};
